import { GENDER_LABELS } from '@pgs/shared';
import type { ReviewField } from './steps/Step9Review';
import type { RegistrationDraft } from './types';

const fmt = (v: string | undefined | null): string => (v && String(v).trim() ? String(v) : '');

const addressLines = (addr: RegistrationDraft['personal']['address']): string => {
  if (!addr) return '';
  return [addr.houseNumber, addr.line1, addr.line2, addr.town, addr.county, addr.postcode]
    .filter((p) => p && String(p).trim())
    .join(', ');
};

export const summarisePersonal = (p: RegistrationDraft['personal']): ReviewField[] => {
  if (!p || Object.keys(p).length === 0) return [];
  const gender = p.gender ? GENDER_LABELS[p.gender as keyof typeof GENDER_LABELS] : '';
  const contact = p.preferredContact
    ? p.preferredContact[0]!.toUpperCase() + p.preferredContact.slice(1)
    : '';
  return [
    { label: 'Title', value: fmt(p.title) },
    { label: 'First name', value: fmt(p.forename) },
    { label: 'Middle name(s)', value: fmt(p.middleName) },
    { label: 'Last name', value: fmt(p.surname) },
    { label: 'Preferred name', value: fmt(p.preferredName) },
    { label: 'Gender', value: gender },
    { label: 'Date of Birth', value: fmt(p.dob) },
    { label: 'Nationality', value: fmt(p.nationality) },
    { label: 'Mobile', value: fmt(p.mobile) },
    { label: 'Email', value: fmt(p.email) },
    { label: 'Address', value: addressLines(p.address) },
    { label: 'NI Number', value: fmt(p.niNumber) },
    { label: 'Preferred Contact', value: contact },
  ].filter((f) => f.value);
};

export const summariseReferral = (r: RegistrationDraft['referral']): ReviewField[] => {
  if (!r?.hasCode) return [];
  if (r.hasCode === 'no') return [{ label: 'Referral code', value: 'No code provided' }];
  return [{ label: 'Referral code', value: fmt(r.code) || '—' }];
};

export const summariseAgency = (a: RegistrationDraft['agency']): ReviewField[] => {
  if (!a || Object.keys(a).length === 0) return [];
  return [
    { label: 'Agency', value: fmt(a.name) },
    { label: 'Branch', value: fmt(a.branch) },
    { label: 'Consultant name', value: fmt(a.consultantName) },
    { label: 'Consultant email', value: fmt(a.consultantEmail) },
    { label: 'Consultant mobile', value: fmt(a.consultantMobile) },
  ].filter((f) => f.value);
};

export const summariseBank = (b: RegistrationDraft['bank']): ReviewField[] => {
  if (!b || Object.keys(b).length === 0) return [];
  const sort = b.sortCode ? b.sortCode.replace(/(\d{2})(\d{2})(\d{2})/, '$1-$2-$3') : '';
  return [
    { label: 'Bank / Building Society', value: fmt(b.bankName) },
    { label: 'Account name', value: fmt(b.accountName) },
    { label: 'Account number', value: fmt(b.accountNumber) },
    { label: 'Sort code', value: sort },
  ].filter((f) => f.value);
};

export const summariseOccupation = (
  o: RegistrationDraft['occupation'],
): ReviewField[] => {
  if (!o) return [];
  return [
    { label: 'Sector', value: fmt(o.sector) },
    { label: 'Job role', value: fmt(o.role) },
    { label: 'Expected start date', value: fmt(o.startDate) },
    { label: 'Construction industry (CIS)', value: o.cis ? 'Yes' : 'No' },
  ].filter((f) => f.value !== '');
};

export const summariseUtr = (u: RegistrationDraft['utr']): ReviewField[] => {
  if (!u) return [];
  if (u.mode === 'na') return [{ label: 'UTR', value: 'Not applicable' }];
  return [{ label: 'UTR number', value: fmt(u.number) }];
};

const PAYMENT_LABELS: Record<'a' | 'b' | 'c', string> = {
  a: 'This is my only job',
  b: 'This is my only job but I have had others since April',
  c: 'I have another job or pension',
};

export const summarisePayment = (p: RegistrationDraft['payment']): ReviewField[] => {
  if (!p?.situation) return [];
  return [{ label: 'Employment situation', value: PAYMENT_LABELS[p.situation] }];
};

export const summariseDocuments = (d: RegistrationDraft['documents']): ReviewField[] => {
  if (!d) return [];
  const out: ReviewField[] = [];
  if (d.evisa?.passed) {
    out.push({ label: 'eVisa share code', value: d.evisa.shareCode });
    out.push({ label: 'eVisa result', value: 'Passed' });
  }
  if (d.photoId) out.push({ label: 'Photo ID', value: d.photoId.type });
  if (d.proofAddress) out.push({ label: 'Proof of Address', value: d.proofAddress.type });
  if (d.rightToWork) out.push({ label: 'Right to Work', value: d.rightToWork.type });
  if (d.niProof) out.push({ label: 'NI Number Proof', value: d.niProof.type });
  if (d.nameChange && d.nameChange.length > 0) {
    out.push({
      label: 'Name Change Documents',
      value: d.nameChange.map((x) => x.type).join(', '),
    });
  }
  return out;
};

export const summariseCisAssessment = (
  c: RegistrationDraft['cisAssessment'],
): ReviewField[] => {
  if (!c) return [];
  return [{ label: 'Assessment', value: 'Completed' }];
};

const TREATMENT_LABELS: Record<'gross' | 'net' | 'higher', string> = {
  gross: 'Gross (0%)',
  net: 'Net standard (20%)',
  higher: 'Higher (30%)',
};

export const summariseCisVerification = (
  v: RegistrationDraft['cisVerification'],
): ReviewField[] => {
  if (!v?.entityType) return [];
  const out: ReviewField[] = [
    {
      label: 'Registering as',
      value: v.entityType === 'company' ? 'Limited company' : 'Sole trader',
    },
  ];
  if (v.entityType === 'company' && v.companyName) {
    out.push({ label: 'Company name', value: v.companyName });
  }
  if (v.result) {
    out.push({
      label: 'HMRC match',
      value: v.result.matchStatus === 'matched' ? 'Matched' : 'Not matched',
    });
    out.push({ label: 'Tax treatment', value: TREATMENT_LABELS[v.result.taxTreatment] });
    out.push({ label: 'Verification number', value: fmt(v.result.verificationNumber) });
  } else {
    out.push({ label: 'HMRC verification', value: 'Not verified (treated as 30%)' });
  }
  return out.filter((f) => f.value);
};
